import type { VirtualFile } from "@pdfking/pdfengine"
import type { FileName } from "@pdfking/structs"
import type { ContentRenderer } from "@pdfking/templates"
import type { StringGUID } from "@pdfking/tfunc"

import type { PdfviewSidebarView } from "#public"
import type { PdfviewState } from "#public"

import { MARKS_GUIDE } from "../types/Guide"

/**
 * The single source of truth for every {@link PdfviewState} field's default —
 * shared by {@link CreatePdfViewer}'s own `props` (so the component works
 * standalone) and by a host router's route defaults (so the URL only encodes
 * a field once it diverges). Language and color scheme are deliberately not
 * here — they're the host's, not the document's (see `lang` in
 * {@link PdfViewerOptions}).
 */
export const PdfviewStateDefaults: PdfviewState = {
  animation: "normal",
  bleed: false,
  content: undefined as ContentRenderer | undefined,
  // 0, not 1: the 3D book's first frame is the closed cover, and each viewer
  // clamps this into its own range anyway (the plot's pager floors it at 1,
  // Book3dViewer's `sync_range` at `frame_start`), so the neutral start is 0.
  current_page: 0,
  files: {} as Record<FileName, VirtualFile | string>,
  // The marks guide flipped off its own default (see GuideVisible: this list
  // holds the guides toggled *away* from `guide.active`, so naming it here
  // makes it active — printable mode, without the preview overlays). A
  // destination is never listed, so every one of them starts shown.
  hidden_guides: [MARKS_GUIDE],
  imposition_data: {},
  page_grouping: "single",
  revision: undefined as StringGUID | undefined,
  sidebar: undefined as PdfviewSidebarView | undefined,
  view_3d: false,
  zoom: 1,
}
